import { Stack } from '@mui/material'
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'react-feather'
import { styled } from '@mui/material/styles'
import { decimalToLocalizedString } from 'ComponentLibrary/src/common/numbers'
import { getTrendColor } from 'ComponentLibrary/src/common/colors'
import { ContentChip } from 'ComponentLibrary/src/piazza/content'

export type TrendIconProps =
    | {
          trend: number
          variant: 'arrow'
          trendUnit?: string
          iconSize?: `${number}px` | number
          inverseColor?: boolean
          locale?: string
      }
    | {
          trend: number
          variant: 'label'
          trendUnit?: string
          iconSize?: never
          inverseColor?: undefined
          locale?: string
      }

export const TrendIcon = ({ trend, variant = 'arrow', trendUnit = '%', iconSize, inverseColor, locale = 'de-DE' }: TrendIconProps) => {
    return variant === 'arrow' ? (
        <ArrowTrend trend={trend} trendUnit={trendUnit} iconSize={iconSize} inverseColor={inverseColor} locale={locale} />
    ) : (
        <LabelTrend trend={trend} trendUnit={trendUnit} locale={locale} />
    )
}

const ArrowTrend = ({
    trend,
    trendUnit = '%',
    iconSize = '20px',
    inverseColor,
    locale,
}: {
    trend: number
    trendUnit?: string
    iconSize?: `${number}px` | number
    inverseColor?: boolean
    locale: string
}) => {
    const formattedTrend = `${decimalToLocalizedString(trend * 100, false, 1, undefined, locale)} ${trendUnit}`

    switch (true) {
        case trend > 0:
            return (
                <StyledStack direction="row" color={getTrendColor(trend, inverseColor)}>
                    <ArrowUpRight height={iconSize} width={iconSize} />
                    {formattedTrend}
                </StyledStack>
            )
        case trend < 0:
            return (
                <StyledStack direction="row" color={getTrendColor(trend, inverseColor)}>
                    <ArrowDownRight height={iconSize} width={iconSize} />
                    {formattedTrend}
                </StyledStack>
            )
        case true:
        default:
            return (
                <StyledStack direction="row" color={getTrendColor(trend, inverseColor)}>
                    <ArrowRight height={iconSize} width={iconSize} />
                    {formattedTrend}
                </StyledStack>
            )
    }
}

const LabelTrend = ({ trend, trendUnit = '%', locale }: { trend: number; trendUnit?: string; locale: string }) => {
    const formattedTrend = `${trend > 0 ? '+' : ''}${decimalToLocalizedString(trend * 100, false, 1, 1, locale)} ${trendUnit}`

    return <ContentChip size={'small'} color={getContentChipColorByTrend(trend)} label={formattedTrend} />
}

const StyledStack = styled(Stack)(({ theme }) => ({
    gap: theme.spacing(1),
    whiteSpace: 'nowrap',
    alignItems: 'center',
}))

const getContentChipColorByTrend = (trend: number) => {
    if (trend > 0) {
        return 'green'
    } else if (trend < 0) {
        return 'red'
    }
    return 'grey'
}
